/* eslint-disable no-console */
import axios from 'axios';
import {createElement} from 'react';

import {ReactComponent as PeerAveragePlus} from '../../../assets/icons/PeerAveragePlus.svg';
import {CLIMATE_DB_BASE_URL, CLIMATE_DB_ENDPOINT_JSON} from '../../../config';
import CRUDMethods from '../../../constants/CRUDConstants';
import type {
  PeerBenchmarkCompanyRow,
  PeerBenchmarkMetricView,
  PeerBenchmarkPillarRow
} from '../../../pages/esgManager/utils/PeerBenchmarking/constants/peerBenchmarkingDefaultData';

import {ensureAIToken} from './aiTokenAPI';

const PILLAR_ORDER = [
  'Environmental',
  'Social',
  'Governance',
  'Strategy'
] as const;

const PILLAR_STYLE: Record<
  string,
  Pick<PeerBenchmarkPillarRow, 'color' | 'pillColor' | 'isEnvironmental'>
> = {
  Environmental: {
    color: '#BAF6EB',
    pillColor: '#98F2E1',
    isEnvironmental: true
  },
  Social: {color: '#E1F3D9', pillColor: '#D3EEC6'},
  Governance: {color: '#FDF6DD', pillColor: '#FBECB8'},
  Strategy: {color: '#E8EAFD', pillColor: '#DDE1FF'}
};

const UNIT_LABEL: Record<string, string> = {
  tCO2e: 'tCO2e',
  tCO2e_per_unit: 'tCO2e/unit',
  tCO2e_per_inr_cr: 'tCO2e/INR Cr',
  GJ: 'GJ',
  kL: 'kL',
  MT: 'MT',
  INR: 'INR',
  hours: 'hours',
  per_million_hours: 'per million hours',
  unit: 'unit'
};

interface PeerBenchmarkPeer {
  entity_id: number;
  legal_name: string;
  value: string | null;
  evidence?: string | null;
}

interface PeerBenchmarkMetric {
  metric_id: string;
  metric_name: string;
  pillar: string;
  unit: string | null;
  data_type: string;
  peer_average: string | null;
  peer_average_evidence?: string | null;
  peer_n: number;
  peers: PeerBenchmarkPeer[];
}

const EVIDENCE_DATA_TYPES = ['qualitative', 'categorical'];

interface PeerBenchmarkResponse {
  peer_group_id?: string;
  peer_group_name?: string;
  fiscal_year?: number;
  year?: string;
  peer_count?: number;
  metrics?: PeerBenchmarkMetric[];
  source?: string;
  industry?: {name?: string | null} | null;
}

export interface EntityPeerBenchmark {
  pillars: PeerBenchmarkPillarRow[];
  peerGroupName: string | null;
}

/**
 * Platform years are either `2025-2026` or a calendar `2025`. The peer
 * benchmark route takes that same value as `fiscal_year`.
 */
const toFiscalYearParam = (selectedYear: string): string | null => {
  const year = selectedYear.trim();

  if (/^\d{4}-\d{4}$/.test(year) || /^\d{4}$/.test(year)) {
    return year;
  }

  return null;
};

const parseAmount = (rawValue: string | null | undefined): number | null => {
  if (rawValue === null || rawValue === undefined || rawValue.trim() === '') {
    return null;
  }

  const amount = Number(rawValue);

  return Number.isFinite(amount) ? amount : null;
};

const formatAmount = (value: number, maximumFractionDigits: number): string => {
  return value.toLocaleString('en-IN', {
    maximumFractionDigits,
    minimumFractionDigits: 0
  });
};

/**
 * Some `pct` metrics are stored as a 0–1 ratio (board diversity `0.25`) and
 * others are already percentages (turnover `8.41`, waste recovered `79.3`).
 * A ratio is only used when every disclosed non-zero value is at most 1.
 */
const isRatioPercent = (metric: PeerBenchmarkMetric): boolean => {
  if (metric.unit !== 'pct' || metric.data_type !== 'quantitative') {
    return false;
  }

  const amounts = [
    metric.peer_average,
    ...metric.peers.map((peer) => peer.value)
  ]
    .map((value) => parseAmount(value))
    .filter((amount): amount is number => amount !== null && amount !== 0);

  return amounts.length > 0 && amounts.every((amount) => Math.abs(amount) <= 1);
};

const formatMetricValue = (
  rawValue: string | null | undefined,
  metric: PeerBenchmarkMetric,
  asRatioPercent: boolean
): string => {
  if (rawValue === null || rawValue === undefined || rawValue.trim() === '') {
    return 'NA';
  }

  const amount = parseAmount(rawValue);

  if (amount === null) {
    return rawValue;
  }

  if (metric.data_type !== 'quantitative') {
    const digits = Number.isInteger(amount) ? 0 : 2;

    return formatAmount(amount, digits);
  }

  if (metric.unit === 'pct') {
    const percent = asRatioPercent ? amount * 100 : amount;

    return `${formatAmount(percent, 2)}%`;
  }

  let digits = 4;

  if (Number.isInteger(amount)) {
    digits = 0;
  } else if (Math.abs(amount) >= 100) {
    digits = 2;
  }

  const label = metric.unit ? UNIT_LABEL[metric.unit] ?? metric.unit : '';
  const formatted = formatAmount(amount, digits);

  return label ? `${formatted} ${label}` : formatted;
};

const toMetricView = (metric: PeerBenchmarkMetric): PeerBenchmarkMetricView => {
  const asRatioPercent = isRatioPercent(metric);
  const showsEvidence = EVIDENCE_DATA_TYPES.includes(metric.data_type);
  const companyData: PeerBenchmarkCompanyRow[] = metric.peers.map((peer) => {
    const value = formatMetricValue(peer.value, metric, asRatioPercent);

    // Qualitative rows render a "+" (empty value) that opens the evidence popup.
    if (showsEvidence) {
      const evidence = peer.evidence?.trim() ?? '';

      return {
        name: peer.legal_name,
        label: peer.legal_name,
        value: '',
        detail: evidence !== '' ? evidence : 'No evidence disclosed.'
      };
    }

    return {
      name: peer.legal_name,
      label: peer.legal_name,
      value,
      detail: value
    };
  });
  const peerLabel = `${metric.peer_n} peer${metric.peer_n === 1 ? '' : 's'}`;
  let disclosed = 'No peer disclosure for this metric.';

  if (metric.peer_n > 0 && metric.data_type === 'quantitative') {
    disclosed = `Disclosed by ${peerLabel}.`;
  } else if (metric.peer_n > 0) {
    disclosed = `Average score from ${peerLabel}.`;
  }

  // Same as the qualitative peer rows: the average is a level score too, so it
  // always shows a "+" that opens the evidence popup.
  if (showsEvidence) {
    const averageEvidence = metric.peer_average_evidence?.trim() ?? '';

    return {
      average: createElement(PeerAveragePlus),
      averageDetail:
        averageEvidence !== '' ? averageEvidence : 'No evidence disclosed.',
      companyData
    };
  }

  return {
    average: formatMetricValue(metric.peer_average, metric, asRatioPercent),
    averageDetail: disclosed,
    companyData
  };
};

const toPillarRows = (
  metrics: PeerBenchmarkMetric[]
): PeerBenchmarkPillarRow[] => {
  const grouped = new Map<string, PeerBenchmarkMetric[]>();

  metrics.forEach((metric) => {
    const pillar = metric.pillar.trim() ? metric.pillar : 'Other';
    const current = grouped.get(pillar) ?? [];
    current.push(metric);
    grouped.set(pillar, current);
  });

  const knownPillars = new Set<string>(PILLAR_ORDER);
  const orderedPillars = [
    ...PILLAR_ORDER.filter((pillar) => grouped.has(pillar)),
    ...Array.from(grouped.keys()).filter((pillar) => !knownPillars.has(pillar))
  ];

  return orderedPillars.map((pillar) => {
    const pillarMetrics = grouped.get(pillar) ?? [];
    const style = PILLAR_STYLE[pillar] ?? {
      color: '#F3F4F8',
      pillColor: '#E6E8EE'
    };
    const metricData: Record<string, PeerBenchmarkMetricView> = {};

    pillarMetrics.forEach((metric) => {
      metricData[metric.metric_id] = toMetricView(metric);
    });

    const firstView = metricData[pillarMetrics[0]?.metric_id];

    return {
      pillar,
      metrics: pillarMetrics.map((metric) => ({
        value: metric.metric_id,
        label: metric.metric_name
      })),
      color: style.color,
      pillColor: style.pillColor,
      isEnvironmental: style.isEnvironmental,
      average: firstView?.average ?? 'NA',
      averageDetail: firstView?.averageDetail ?? '',
      companyData: firstView?.companyData ?? [],
      metricData
    };
  });
};

export const getEntityPeerBenchmark = async (
  companyId: string,
  selectedYear: string,
  peerIds?: number[]
): Promise<EntityPeerBenchmark | null> => {
  const entityId = companyId.trim();
  const fiscalYear = toFiscalYearParam(selectedYear);
  const token = await ensureAIToken();

  if (!entityId || !fiscalYear || !token) {
    return null;
  }

  const params: Record<string, string> = {fiscal_year: fiscalYear};

  // `peer_ids` and `default_count` are mutually exclusive with each other and
  // with `peer_group_id`; sending none returns the whole main group, which is
  // the behaviour this page had before peer selection existed.
  if (peerIds !== undefined && peerIds.length > 0) {
    params.peer_ids = peerIds.join(',');
  }

  try {
    const benchmarkUrl = [
      CLIMATE_DB_BASE_URL,
      CLIMATE_DB_ENDPOINT_JSON.entities,
      '/',
      encodeURIComponent(entityId),
      CLIMATE_DB_ENDPOINT_JSON.peerBenchmark
    ].join('');
    const response = await axios.request<PeerBenchmarkResponse>({
      method: CRUDMethods.GET,
      url: benchmarkUrl,
      params,
      headers: {Authorization: `Bearer ${token}`}
    });
    const peerGroupName = response.data?.industry?.name ?? '';

    return {
      pillars: toPillarRows(response.data?.metrics ?? []),
      peerGroupName: peerGroupName === '' ? null : peerGroupName
    };
  } catch (err) {
    console.log('peer benchmark err:', err);

    return null;
  }
};
